// components/onboarding/login/login-component.tsx
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema, LoginFormData } from "@/lib/schemas/authSchema";
import PasswordInput from "@/components/ui/password-input";
import { motion } from "framer-motion";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/axiosInstance";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Instagram } from "lucide-react";
import { FcGoogle } from "react-icons/fc";
import { ADMIN_URL } from "@/lib/config/urls";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export default function AccountLogin() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authMethodModal, setAuthMethodModal] = useState<{ open: boolean; method: "GOOGLE" | "LOCAL" | null }>({ open: false, method: null });
  const { user, setUser } = useAuth();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Handle URL toast parameters
  useEffect(() => {
    const toastType = searchParams.get("toast");
    const message = searchParams.get("msg");

    if (toastType && message) {
      const decodedMessage = decodeURIComponent(message);
      if (toastType === "error") {
        toast.error(decodedMessage);
      } else if (toastType === "success") {
        toast.success(decodedMessage);
      }
      
      // Clean up URL parameters
      router.replace("/auth/login");
    }
  }, [searchParams, router]);

  useEffect(() => {
    if (user) {
      // Check if there's a redirect parameter
      const redirectParam = searchParams.get("redirect");
      if (redirectParam) {
        router.replace(redirectParam);
      } else {
        // Redirect based on user role - Admin goes to subdomain
        if (user.role?.toUpperCase() === "ADMIN") {
          window.location.href = ADMIN_URL;
        } else {
          router.replace("/dashboard");
        }
      }
    }
  }, [user, router, searchParams]);

  const onSubmit = async (data: LoginFormData) => {
    setIsSubmitting(true);
    try {
      const res = await api.post("/auth/login", data);
      setUser(res.data.user);

      toast.success("Logged in successfully!");
      
      // Check if there's a redirect parameter
      const redirectParam = searchParams.get("redirect");
      if (redirectParam) {
        router.push(redirectParam);
      } else {
        // Redirect based on user role - Admin goes to subdomain
        if (res.data.user.role?.toUpperCase() === "ADMIN") {
          window.location.href = ADMIN_URL;
        } else {
          router.push("/dashboard");
        }
      }
    } catch (error: any) {
      const status = error.response?.status;
      const msg = error.response?.data?.msg || "Login failed";
      const requiresVerification = error.response?.data?.requiresVerification;
      const authProvider = error.response?.data?.authProvider;

      if (status === 401 && requiresVerification) {
        toast.error(msg, {
          duration: 5000,
          action: {
            label: "Resend Email",
            onClick: async () => {
              try {
                await api.post("/auth/resend-verify", { email: data.email });
                toast.success(
                  "Verification email has been resent! Check your inbox."
                );
              } catch (resendError: any) {
                toast.error(
                  resendError.response?.data?.msg ||
                    "Failed to resend verification email. Please try again."
                );
              }
            },
          },
        });
      } else if (authProvider === "GOOGLE") {
        setAuthMethodModal({ open: true, method: "GOOGLE" });
      } else if (status === 400 || status === 401) {
        toast.error(msg);
        setError("password", { message: "Invalid credentials" });
      } else {
        toast.error("Network error. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = () => {
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || 'http://localhost:5000/api';
    window.location.href = `${backendUrl}/auth/google/login`;
  };

  return (
    <div className="text-center w-full max-w-lg">
      {/* Auth method conflict modal */}
      <Dialog open={authMethodModal.open} onOpenChange={(o) => setAuthMethodModal({ open: o, method: null })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Wrong sign-in method</DialogTitle>
            <DialogDescription>
              {authMethodModal.method === "GOOGLE"
                ? "This account was created with Google. Please use the \"Continue with Google\" button to sign in."
                : "This account was registered with an email and password. Please use your email and password to sign in."}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-2">
            {authMethodModal.method === "GOOGLE" && (
              <Button
                className="w-full flex items-center gap-2"
                onClick={() => { setAuthMethodModal({ open: false, method: null }); handleGoogleLogin(); }}
              >
                <FcGoogle className="w-5 h-5" />
                Continue with Google
              </Button>
            )}
            <Button variant="outline" className="w-full" onClick={() => setAuthMethodModal({ open: false, method: null })}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <motion.h1
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-3xl font-semibold text-gray-800"
      >
        Welcome back!
      </motion.h1>
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-2 text-sm text-gray-600"
      >
        Log back into your Ren Power account.
      </motion.p>

      {/* Google Login */}
      <Button
        type="button"
        variant="outline"
        className="w-full flex items-center justify-center gap-3 my-6"
        onClick={handleGoogleLogin}
      >
        <FcGoogle className="w-5 h-5" />
        Continue with Google
      </Button>

      {/* Divider */}
      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300"></div>
        </div>
        <div className="relative flex justify-center text-xs">
          <span className="bg-white px-2 text-gray-500">or</span>
        </div>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="mt-8 space-y-5 text-left"
      >
        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email
          </label>
          <input
            type="email"
            {...register("email")}
            placeholder="you@email.com"
            disabled={isSubmitting}
            className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-green-500 ${
              errors.email ? "border-red-500" : "border-gray-200"
            }`}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <PasswordInput
            control={control as any}
            name="password"
            label="Password"
            showStrengthMeter={false}
            errorMessage={errors.password?.message}
            disabled={isSubmitting}
            className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-green-500 ${
              errors.password ? "border-red-500" : "border-gray-200"
            }`}
          />
          <Link
            href="/auth/reset-password"
            className="text-sm self-end text-[#17BC2E] hover:underline mt-1 block"
          >
            Forgot password?
          </Link>
        </div>

        {/* Submit Button */}
        <div className="mt-10 flex flex-col items-center justify-between md:flex-row">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="text-sm text-gray-700"
          >
            Don't have an account?{" "}
            <Link
              href="/auth/register"
              className="font-medium text-[#002301] hover:underline"
            >
              Register here
            </Link>
          </motion.p>
          <motion.button
            whileTap={{ scale: 0.95 }}
            type="submit"
            disabled={isSubmitting}
            className={`px-6 py-3 rounded-full font-semibold text-sm ${
              isSubmitting
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-[#FFB600] text-[#002301] hover:bg-[#17BC2E] hover:text-white"
            } transition-all duration-300`}
          >
            {isSubmitting ? "Logging In..." : "Log In"}
          </motion.button>
        </div>
      </form>
    </div>
  );
}
